import React from 'react';
import PropTypes from 'prop-types';
import Grid from '@material-ui/core/Grid';
import { Formik, Form as FormikForm, useFormikContext, getIn } from 'formik';
import * as Yup from 'yup';
import { Button, InputAdornment } from '@material-ui/core';

import deleteIcon from 'core/assets/svg/delete-icon.svg';
import useSession from 'core/hooks/use-session';
import { ActionButton } from 'core/components/action-button';
import { FormField } from 'core/components/form';
import { FieldWithTooltip } from 'core/components/field-with-tooltip';
import { noPrimaryAddressTest } from 'common/helpers/yup-helpers';
import { useRouteHelper } from 'core/hooks/use-route-helper';

import useStyles from './properties-form.styles';

function AddProperty({ disabled, propertyType, onAdd }) {
  const { viewOnly } = useSession();
  const classes = useStyles();
  const { inPolicyRoute } = useRouteHelper();
  const { values } = useFormikContext();

  const primaryAddress = inPolicyRoute ? values.home?.homeLocation : values.correctedAddress;

  return (
    <Formik
      initialValues={{
        [propertyType]: {
          custom: false,
          displayAddress: '',
          address: '',
          address2: '',
          city: '',
          state: '',
          zip: ''
        }
      }}
      validationSchema={() => {
        return Yup.object().shape({
          [propertyType]: Yup.object().shape({
            displayAddress: Yup.string().test(
              'noPrimaryAddress',
              'Cannot include primary address',
              noPrimaryAddressTest({ primaryAddress, propertyType })
            ),
            custom: Yup.boolean(),
            address: Yup.string().required('Address is required'),
            address2: Yup.string().nullable(),
            city: Yup.string().required('City is required'),
            state: Yup.string().required('State is required'),
            zip: Yup.string().required('Zip is required')
          })
        });
      }}
      onSubmit={(values) => {
        onAdd(values[propertyType]);
      }}
    >
      {({ values, errors, isSubmitting, setFieldValue, handleSubmit }) => {
        return (
          <FormikForm className={classes.addFormContainer} disabled={disabled || viewOnly}>
            <Grid container className={classes.gridContainer}>
              <Grid item xs={12} spacing={2} className={classes.gridItem}>
                <FormField
                  id={`${propertyType}.displayAddress`}
                  name={`${propertyType}.displayAddress`}
                  type="address"
                  mode="dark"
                  label="Address search"
                  optionsLimit={4}
                  description={
                    getIn(errors, `${propertyType}.displayAddress`)
                      ? ''
                      : 'Search for an address in our database by entering Address, City, State, and ZIP code'
                  }
                  descriptionClassName={classes.description}
                  propertyType={propertyType}
                  InputProps={{
                    startAdornment: values[propertyType].displayAddress && (
                      <InputAdornment className={classes.inputAdornment} position="end">
                        <ActionButton
                          onClick={(e) => {
                            e.stopPropagation();

                            setFieldValue(`${propertyType}.displayAddress`, '');
                            setFieldValue(`${propertyType}.address`, '');
                            setFieldValue(`${propertyType}.address2`, '');
                            setFieldValue(`${propertyType}.city`, '');
                            setFieldValue(`${propertyType}.state`, '');
                            setFieldValue(`${propertyType}.zip`, '');
                          }}
                          className={classes.deleteButton}
                          icon={deleteIcon}
                          size="tiny"
                        />
                      </InputAdornment>
                    )
                  }}
                />
              </Grid>
              {/* Suggested address */}
              {!values[propertyType].custom && values[propertyType].address && (
                <Grid item xs={12} spacing={2} className={classes.relativeGridItem}>
                  <FieldWithTooltip
                    id={`${propertyType}.address2`}
                    name={`${propertyType}.address2`}
                    type="string"
                    mode="dark"
                    label="Apt, unit, floor, building, etc. (optional)"
                    tooltip="Apt, unit, floor, building, etc., are secondary address designators, (AKA Address line 2), that help ensure we have the right additional address details."
                    labelClassName={classes.tooltipFieldLabel}
                    showLabelText={false}
                  />
                </Grid>
              )}

              {/* Custom address */}
              {values[propertyType].custom && (
                <>
                  <Grid item xs={6} spacing={2}>
                    <FormField
                      id={`${propertyType}.address`}
                      name={`${propertyType}.address`}
                      type="string"
                      mode="dark"
                      label="Address"
                    />
                  </Grid>
                  <Grid item xs={6} spacing={2} className={classes.relativeGridItem}>
                    <FieldWithTooltip
                      id={`${propertyType}.address2`}
                      name={`${propertyType}.address2`}
                      type="string"
                      mode="dark"
                      label="Apt, unit, floor, building, etc. (optional)"
                      tooltip="Apt, unit, floor, building, etc., are secondary address designators, (AKA Address line 2), that help ensure we have the right additional address details."
                      labelClassName={classes.tooltipFieldLabel}
                      showLabelText={false}
                    />
                  </Grid>
                  <Grid item xs={4} spacing={2}>
                    <FormField
                      id={`${propertyType}.city`}
                      name={`${propertyType}.city`}
                      type="string"
                      mode="dark"
                      label="City"
                    />
                  </Grid>
                  <Grid item xs={4} spacing={2}>
                    <FormField
                      id={`${propertyType}.state`}
                      name={`${propertyType}.state`}
                      type="state"
                      mode="dark"
                      label="State"
                    />
                  </Grid>
                  <Grid item xs={4} spacing={2} className={classes.gridItem}>
                    <FormField
                      id={`${propertyType}.zip`}
                      name={`${propertyType}.zip`}
                      type="string"
                      mode="dark"
                      label="Zip"
                    />
                  </Grid>
                </>
              )}
              {(values[propertyType].address || values[propertyType].custom) && (
                <Grid container justifyContent="space-between" alignItems="center" className={classes.singleBottom}>
                  <Button
                    // type="submit"
                    mode="big"
                    variant="contained"
                    color="secondary"
                    className={classes.button}
                    disabled={disabled || viewOnly || isSubmitting}
                    onClick={handleSubmit}
                  >
                    Add additional residence
                  </Button>
                </Grid>
              )}
            </Grid>
          </FormikForm>
        );
      }}
    </Formik>
  );
}

AddProperty.propTypes = {
  disabled: PropTypes.bool,
  propertyType: PropTypes.string.isRequired,
  onAdd: PropTypes.func.isRequired
};

AddProperty.defaultProps = {
  disabled: false,
  addContent: null
};

export default AddProperty;
